/**
 * The Expense split rule — one home for "who owes what" on a single Expense,
 * shared by the backend solver/validator and the webapp's Balances derivations.
 *
 * Wire units: `amount` and Value `splitAmount`s are integer cents; Percent
 * `splitAmount`s are whole integer percents (60 = 60%) summing to exactly 100.
 *
 * Rounding (CONTEXT.md, Expense): each share rounds **down**, and the payer
 * absorbs the remainder — they get back slightly less, never more.
 */
/** A Value/Percent participant is missing its splitAmount — a wire-contract violation. */
export class SplitAmountMissingError extends Error {
}
/**
 * One participant's floored share of the expense, in the smallest stored unit.
 * Throws {@link SplitAmountMissingError} when a Value/Percent participant has
 * no splitAmount.
 */
export const participantShare = (expense, participant) => {
    if (expense.splitType === 'Equally') {
        return Math.floor(expense.amount / expense.participants.length);
    }
    if (expense.splitType === 'Percent') {
        if (participant.splitAmount == null) {
            throw new SplitAmountMissingError('Participant split amount must be provided');
        }
        // Multiply before dividing: dividing first rounds `amount / 100` to a
        // binary float and loses a unit on ~1.7% of splits (100% of 29 gave 28).
        return Math.floor((participant.splitAmount * expense.amount) / 100);
    }
    if (participant.splitAmount == null) {
        throw new SplitAmountMissingError('Amount split amount must be provided');
    }
    return participant.splitAmount;
};
const NEGATIVE_SPLIT_AMOUNT = {
    code: 'NEGATIVE_SPLIT_AMOUNT',
    message: 'Split amount cannot be negative',
};
/**
 * The write-path gate: is this a well-formed split per the wire contract?
 * Returns null when valid, or the first failure in precedence order
 * (participants → amount → per-type sum rules).
 */
export const validateSplit = (expense) => {
    if (expense.participants.length === 0) {
        return { code: 'NO_PARTICIPANTS', message: 'Expense must have at least one participant' };
    }
    if (expense.amount <= 0) {
        return { code: 'NON_POSITIVE_AMOUNT', message: 'Expense amount must be positive' };
    }
    if (expense.splitType === 'Value') {
        let totalSplit = 0;
        for (const participant of expense.participants) {
            // Falsy check on purpose: the wire contract treats a 0 Value split as absent.
            if (!participant.splitAmount) {
                return { code: 'MISSING_SPLIT_AMOUNT', message: 'Split amount must be provided' };
            }
            // Without this, negatives that happen to sum to the amount pass the
            // sum rule and credit a participant instead of charging them.
            if (participant.splitAmount < 0) {
                return NEGATIVE_SPLIT_AMOUNT;
            }
            totalSplit += participant.splitAmount;
        }
        if (totalSplit !== expense.amount) {
            return {
                code: 'VALUE_SUM_MISMATCH',
                message: 'Total split amount must be equal to the expense amount',
            };
        }
    }
    if (expense.splitType === 'Percent') {
        let totalSplit = 0;
        for (const participant of expense.participants) {
            // Null check (not falsy): an explicit 0% participant is allowed.
            if (participant.splitAmount == null) {
                return { code: 'MISSING_SPLIT_AMOUNT', message: 'Split amount must be provided' };
            }
            if (participant.splitAmount < 0) {
                return NEGATIVE_SPLIT_AMOUNT;
            }
            totalSplit += participant.splitAmount;
        }
        if (totalSplit !== 100) {
            return { code: 'PERCENT_SUM_MISMATCH', message: 'Total split amount must be equal to 100%' };
        }
    }
    return null;
};
/** A user's floored share, or 0 when they are not a participant. */
export const userShare = (expense, userId) => {
    const participant = expense.participants.find(p => p.userId === userId);
    return participant ? participantShare(expense, participant) : 0;
};
/**
 * Signed net impact per userId: participants owe their floored share (−), the
 * payer is credited the sum of those shares (+), so impacts sum to exactly zero.
 */
export const expenseImpacts = (expense) => {
    const impacts = new Map();
    let payerOwed = 0;
    for (const participant of expense.participants) {
        const share = participantShare(expense, participant);
        impacts.set(participant.userId, (impacts.get(participant.userId) ?? 0) - share);
        payerOwed += share;
    }
    impacts.set(expense.paidByUserId, (impacts.get(expense.paidByUserId) ?? 0) + payerOwed);
    return impacts;
};
//# sourceMappingURL=expenseSplit.js.map